<script setup>
import { useForm } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, onMounted, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ChecklistChangeList from '@/components/admin/report-qc/ChecklistChangeList.vue'
import ChecklistHistoryDrawer from '@/components/admin/report-qc/ChecklistHistoryDrawer.vue'
import ChecklistItemDrawer from '@/components/admin/report-qc/ChecklistItemDrawer.vue'
import ChecklistPreviewPanel from '@/components/admin/report-qc/ChecklistPreviewPanel.vue'
import ChecklistTrial from '@/components/admin/report-qc/ChecklistTrial.vue'
import { useChecklistPreview } from '@/composables/useChecklistPreview.js'
import { useUnsavedGuard } from '@/composables/useUnsavedGuard.js'

/**
 * RED-386 + RED-361: Kontrollnimekiri as ONE list (option B, 2026-10-15). The
 * items in their two blocks, each with the question(s) to the engine that check
 * it; one drawer edits an item and its questions; the send order is a collapsed
 * card of its own; one save bar: every save is a new checklist version with the
 * full draft and a note. The data model is unchanged: an item text is the rule
 * (every item goes into the Üldjuhis of every request), a question text is its
 * own request.
 *
 * The server is the truth. The draft goes to the preview endpoint on every
 * change, and the answer brings back the save-rule problems (D1, D4, D5, the
 * index guard, …) shown inline, the automatic sentence of each question and
 * the before → after counts. A hand-made request meets the same rules as a 422.
 * Nothing is ever deleted: `Aktiivne` off instead.
 *
 * RED-361 (dev → live): `Laadi üles` in Versioonid hands over a downloaded
 * version. Its items, questions and Üldjuhis replace the draft (unsaved), and a
 * dialog lists what the save would change (the preview's `changes`). The hash
 * gate: the preview hash of the draft must equal the file's hash, or this
 * server would send the engine something else than the source did (a
 * hand-edited file, or different prompt code), and saving is disabled. The
 * save is the ordinary PUT with a prefilled note, so every save rule applies.
 */
const props = defineProps({
  checklist: { type: Object, required: true },
  versions: { type: Array, default: undefined },
  trial: { type: Object, required: true },
})

const { t } = useI18n()
const toast = useToast()

const clone = value => JSON.parse(JSON.stringify(value))

const baseline = () => ({ items: clone(props.checklist.items), queries: clone(props.checklist.queries) })

const draft = reactive(baseline())
const showPreview = ref(false)
const historyOpen = ref(false)

// RED-361: the uploaded file (`Laadi üles`) and its Üldjuhis, while its draft is unsaved.
const imported = ref(null)
const importedFrame = ref(null)
const importOpen = ref(false)

const form = useForm({ note: '' })

// The draft as the server reads it: the Üldjuhis only when a file brought one.
const payload = () => ({ items: draft.items, queries: draft.queries, ...(importedFrame.value ? { frame: importedFrame.value } : {}) })

const { preview, loading: previewLoading, failed: previewFailed } = useChecklistPreview(payload)

const dirty = computed(
  () =>
    JSON.stringify({ items: draft.items, queries: draft.queries }) !== JSON.stringify(baseline()) ||
    (importedFrame.value !== null && JSON.stringify(importedFrame.value) !== JSON.stringify(props.checklist.frame)),
)

useUnsavedGuard(() => dirty.value || form.note.trim() !== '')

const resetDraft = () => {
  const fresh = baseline()
  draft.items.splice(0, draft.items.length, ...fresh.items)
  draft.queries.splice(0, draft.queries.length, ...fresh.queries)
  imported.value = null
  importedFrame.value = null
  importOpen.value = false
  form.reset()
  form.clearErrors()
}

// A save or a restore brings new props: they become the new baseline.
watch(() => [props.checklist.versionNumber, props.checklist.items, props.checklist.queries], resetDraft, { deep: true })

const problems = computed(() => preview.value?.problems ?? [])

const problemsFor = (scope, id) => problems.value.filter(problem => problem.scope === scope && problem.ref === id)

const itemTitle = number => draft.items.find(item => item.number === number)?.title ?? ''

const queryTitle = query => (query.covers_items.length ? itemTitle(query.covers_items[0]) : t('messages.admin.report_qc.checklist.no_item_chosen'))

// PrimeVue tooltip, never `title=` (design-spec D-6): dark, max 360 px, 300 ms delay.
const tip = value => ({ value, showDelay: 300, pt: { root: { style: { maxWidth: '360px' } } } })

// "15", "15 ja 16", "10, 11 ja 12": the item numbers as one Estonian phrase.
const numberList = numbers => (numbers.length > 1 ? `${numbers.slice(0, -1).join(', ')} ja ${numbers.at(-1)}` : String(numbers[0] ?? ''))

provide('checklistDraft', { draft, preview, problemsFor, queryTitle, tip, numberList })

const blocks = computed(() => [
  { key: 'standard', label: t('messages.admin.report_qc.checklist.block_standard'), items: draft.items.filter(item => item.block === 'standard') },
  { key: 'praktika', label: t('messages.admin.report_qc.checklist.block_praktika'), items: draft.items.filter(item => item.block !== 'standard') },
])

const orderedQueries = computed(() => [...draft.queries].sort((a, b) => a.sort_order - b.sort_order))

const activeQueryCount = computed(() => draft.queries.filter(query => query.active).length)

// The questions shown inside an item row: the active ones that check it, in send order.
const activeCovering = number => orderedQueries.value.filter(query => query.active && query.covers_items.includes(number))

const sharedLabel = (query, number) => {
  const others = query.covers_items.filter(other => other !== number)

  if (others.length === 0) {
    return null
  }

  return t(`messages.admin.report_qc.checklist.${others.length === 1 ? 'row_shared' : 'row_shared_many'}`, { n: numberList(others) })
}

const firstLine = text => text.split('\n').find(line => line.trim() !== '') ?? ''

const orderOpen = ref(false)

// A question with a save-rule problem keeps the send order open: its row shows the message.
const orderExpanded = computed(() => orderOpen.value || problems.value.some(problem => problem.scope === 'query'))

// Reorder: swap the two rows' sort_order, so an unchanged save keeps the hash.
const move = (query, step) => {
  const list = orderedQueries.value
  const other = list[list.indexOf(query) + step]

  if (other) {
    ;[query.sort_order, other.sort_order] = [other.sort_order, query.sort_order]
  }
}

// The drawer edits the draft live; closing without applying puts the snapshot back.
const itemDrawer = reactive({ visible: false, number: null, isNew: false, focusKey: null, snapshot: null, applied: false, feedbackVersion: null })

const snapshot = () => ({ items: clone(draft.items), queries: clone(draft.queries) })

const restore = saved => {
  draft.items.splice(0, draft.items.length, ...saved.items)
  draft.queries.splice(0, draft.queries.length, ...saved.queries)
}

const openItem = (number, focusKey = null) =>
  Object.assign(itemDrawer, { visible: true, number, isNew: false, focusKey, snapshot: snapshot(), applied: false, feedbackVersion: null })

const addItem = () => {
  const saved = snapshot()
  const number = Math.max(props.checklist.nextNumber, ...draft.items.map(item => item.number + 1))
  draft.items.push({ number, title: '', body: '', block: 'praktika', active: true })
  Object.assign(itemDrawer, { visible: true, number, isNew: true, focusKey: null, snapshot: saved, applied: false, feedbackVersion: null })
}

// A send-order row opens the drawer of the question's first item, at the question; without items, the question alone.
const openQuery = query => openItem(query.covers_items[0] ?? null, query.key)

// RED-361: `Ava küsimus` on a Tagasiside row lands here with `?query=<key>&version=<n>`.
// Read once, then dropped from the address: a save (`back()`) or a reload must not open it again.
onMounted(() => {
  const url = new URL(window.location.href)
  const query = draft.queries.find(row => row.key === url.searchParams.get('query'))
  const version = Number(url.searchParams.get('version')) || null

  if (url.searchParams.has('query') || url.searchParams.has('version')) {
    url.searchParams.delete('query')
    url.searchParams.delete('version')
    window.history.replaceState(window.history.state, '', url)
  }

  if (query) {
    openQuery(query)
    itemDrawer.feedbackVersion = version
  }
})

const applyDrawer = state => {
  state.applied = true
  state.visible = false
}

const cancelDrawer = state => {
  if (!state.applied && state.snapshot) {
    restore(state.snapshot)
  }

  state.snapshot = null
}

const impact = computed(() =>
  preview.value
    ? t('messages.admin.report_qc.checklist.impact', {
        c0: preview.value.before.calls,
        c1: preview.value.calls,
        r0: preview.value.before.rounds,
        r1: preview.value.rounds.length,
      })
    : '',
)

const overLimit = computed(() => !!preview.value && (preview.value.overCap || preview.value.overDeadline))

// The hash gate of an upload: the draft must send the engine exactly what the file's server sent.
const importHashMatches = computed(() => !!preview.value && preview.value.hash === imported.value?.hash)

const canSave = computed(
  () => dirty.value && !form.processing && !previewLoading.value && problems.value.length === 0 && (imported.value === null || importHashMatches.value),
)

const hostOf = url => {
  try {
    return new URL(url).host
  } catch {
    return String(url ?? '')
  }
}

const dateOf = iso => (iso ? new Intl.DateTimeFormat('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso)) : '')

const importFile = file => {
  const { items, queries, frame } = clone(file.snapshot)
  draft.items.splice(0, draft.items.length, ...items)
  draft.queries.splice(0, draft.queries.length, ...queries)
  importedFrame.value = frame
  imported.value = { host: hostOf(file.source), number: file.number, hash: file.hash, date: dateOf(file.created_at), settingsVersion: file.settingsVersion }
  form.clearErrors()
  form.note = t('messages.admin.report_qc.transfer.note', { host: imported.value.host, n: file.number, hash: file.hash, date: imported.value.date })
  importOpen.value = true
}

// Server errors that no row shows (a hand-edited state, a race with another admin).
const otherErrors = computed(() => Object.entries(form.errors).filter(([key]) => key !== 'note').map(([, message]) => message))

const save = () => {
  form
    .transform(data => ({ ...data, ...payload() }))
    .put(route('admin.report-qc.checklist.update'), {
      preserveScroll: true,
      onSuccess: page => {
        toast.add({
          severity: 'success',
          summary: t('messages.admin.report_qc.settings.saved'),
          detail: t('messages.admin.report_qc.uldjuhis.saved_detail', { n: page.props.checklist.versionNumber }),
          life: 4000,
        })
      },
    })
}
</script>

<template>
  <div class="flex flex-col gap-6" data-testid="checklist-editor">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-xl font-medium">{{ t('messages.admin.report_qc.nav.kontrollnimekiri') }}</h2>
        <p v-if="checklist.versionNumber" class="text-sm text-gray-600" data-testid="checklist-version">
          {{ t('messages.admin.report_qc.uldjuhis.version', { n: checklist.versionNumber }) }}
          <i v-if="checklist.hash" v-tooltip.top="t('messages.admin.report_qc.uldjuhis.version_hash_tooltip', { hash: checklist.hash })" class="pi pi-info-circle ml-1 text-xs text-gray-400" :aria-label="t('messages.admin.report_qc.uldjuhis.version_hash_tooltip', { hash: checklist.hash })" data-testid="checklist-version-hash" />
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <ToggleSwitch v-model="showPreview" input-id="rqc-checklist-preview" data-testid="checklist-preview-toggle" />
          <label for="rqc-checklist-preview" class="text-sm">{{ t('messages.admin.report_qc.preview.toggle') }}</label>
        </div>
        <Button
          :label="t('messages.admin.report_qc.history.button')"
          icon="pi pi-history"
          severity="secondary"
          outlined
          data-testid="checklist-history-open"
          @click="historyOpen = true"
        />
      </div>
    </div>

    <div class="flex flex-col gap-6 xl:flex-row xl:items-start">
      <div class="flex min-w-0 flex-1 flex-col gap-6">
        <section class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-gray-800" data-testid="checklist-callout">
          <h3 class="mb-2 font-medium">{{ t('messages.admin.report_qc.checklist.callout_title') }}</h3>
          <div class="grid gap-3 md:grid-cols-2">
            <p>{{ t('messages.admin.report_qc.checklist.callout_item') }}</p>
            <p>{{ t('messages.admin.report_qc.checklist.callout_query') }}</p>
          </div>
          <ul class="mt-2 flex flex-col gap-1 text-gray-700">
            <li class="flex gap-2"><i class="pi pi-arrow-right mt-1 text-xs text-blue-500" aria-hidden="true" />{{ t('messages.admin.report_qc.checklist.callout_rule1') }}</li>
            <li class="flex gap-2"><i class="pi pi-arrow-right mt-1 text-xs text-blue-500" aria-hidden="true" />{{ t('messages.admin.report_qc.checklist.callout_rule2') }}</li>
          </ul>
        </section>

        <Card v-for="block in blocks" :key="block.key" :data-testid="`checklist-block-${block.key}`">
          <template #title>
            <span class="text-base font-medium tracking-wide text-gray-700">{{ block.label }}</span>
          </template>
          <template #content>
            <ul class="flex flex-col divide-y divide-gray-100">
              <li
                v-for="item in block.items"
                :key="item.number"
                class="flex cursor-pointer items-start gap-3 py-3 hover:bg-gray-50"
                :data-testid="`checklist-item-${item.number}`"
                @click="openItem(item.number)"
              >
                <span class="w-6 shrink-0 text-right font-mono text-sm text-gray-500">{{ item.number }}</span>
                <div class="min-w-0 flex-1" :class="{ 'opacity-55': !item.active }">
                  <p class="font-medium">{{ item.title }}</p>
                  <p class="line-clamp-2 text-sm text-gray-600">{{ item.body }}</p>
                  <p v-if="!item.active" class="mt-1 text-xs text-gray-500" :data-testid="`checklist-item-note-${item.number}`">
                    {{ t('messages.admin.report_qc.checklist.e1') }}
                  </p>
                  <template v-else-if="activeCovering(item.number).length">
                    <button
                      v-for="query in activeCovering(item.number)"
                      :key="query.key"
                      type="button"
                      class="mt-2 block w-full min-w-0 rounded-md border-l-2 border-blue-300 bg-gray-50 px-3 py-1.5 text-left hover:bg-gray-100"
                      :data-testid="`checklist-item-query-${item.number}-${query.key}`"
                      @click.stop="openItem(item.number, query.key)"
                    >
                      <span class="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        {{ t('messages.admin.report_qc.checklist.row_query_label') }}
                        <span class="font-mono font-medium text-gray-700">{{ query.key }}</span>
                        <span
                          v-if="sharedLabel(query, item.number)"
                          class="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700"
                          :data-testid="`checklist-item-shared-${item.number}-${query.key}`"
                        >{{ sharedLabel(query, item.number) }}</span>
                      </span>
                      <span class="block truncate text-sm text-gray-700">{{ firstLine(query.prompt) }}</span>
                    </button>
                  </template>
                  <p v-else class="mt-1 flex items-center gap-1 text-xs text-amber-700" :data-testid="`checklist-item-note-${item.number}`">
                    <i class="pi pi-exclamation-triangle" aria-hidden="true" />{{ t('messages.admin.report_qc.checklist.row_off') }}
                  </p>
                  <p
                    v-for="problem in problemsFor('item', item.number)"
                    :key="problem.field + problem.code"
                    class="mt-1 text-sm text-red-600"
                    :data-testid="`checklist-item-error-${item.number}`"
                  >
                    {{ problem.message }}
                  </p>
                </div>
                <ToggleSwitch
                  v-model="item.active"
                  :aria-label="t('messages.admin.report_qc.checklist.active')"
                  :data-testid="`checklist-item-switch-${item.number}`"
                  @click.stop
                />
              </li>
            </ul>
          </template>
        </Card>

        <div>
          <Button
            :label="t('messages.admin.report_qc.checklist.add_item')"
            icon="pi pi-plus"
            severity="secondary"
            outlined
            class="w-auto!"
            data-testid="checklist-add-item"
            @click="addItem"
          />
        </div>

        <Card data-testid="checklist-queries">
          <template #title>
            <button
              type="button"
              class="flex w-full items-center gap-2 text-left"
              :aria-expanded="orderExpanded"
              data-testid="checklist-order-toggle"
              @click="orderOpen = !orderExpanded"
            >
              <i class="pi text-sm text-gray-500" :class="orderExpanded ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true" />
              <span class="text-base font-medium">{{ t('messages.admin.report_qc.checklist.order_title', { n: draft.queries.length }) }}</span>
              <span class="text-sm font-normal text-gray-500">{{ t('messages.admin.report_qc.checklist.queries_active', { n: activeQueryCount }) }}</span>
            </button>
          </template>
          <template #subtitle>
            <span :class="{ invisible: !preview }" data-testid="checklist-order-hint">{{ t('messages.admin.report_qc.checklist.order_hint', { n: preview?.concurrency ?? '' }) }}</span>
          </template>
          <template #content>
            <ul v-if="orderExpanded" class="flex flex-col divide-y divide-gray-100">
              <li
                v-for="(query, index) in orderedQueries"
                :key="query.key"
                class="flex cursor-pointer items-center gap-3 py-2 hover:bg-gray-50"
                :data-testid="`checklist-query-${query.key}`"
                @click="openQuery(query)"
              >
                <div class="flex flex-col" @click.stop>
                  <Button
                    icon="pi pi-chevron-up"
                    text
                    size="small"
                    :disabled="index === 0"
                    :aria-label="t('messages.admin.report_qc.checklist.move_up')"
                    v-tooltip.top="tip(t('messages.admin.report_qc.checklist.move_up'))"
                    :data-testid="`checklist-query-up-${query.key}`"
                    @click="move(query, -1)"
                  />
                  <Button
                    icon="pi pi-chevron-down"
                    text
                    size="small"
                    :disabled="index === orderedQueries.length - 1"
                    :aria-label="t('messages.admin.report_qc.checklist.move_down')"
                    v-tooltip.top="tip(t('messages.admin.report_qc.checklist.move_down'))"
                    :data-testid="`checklist-query-down-${query.key}`"
                    @click="move(query, 1)"
                  />
                </div>
                <span class="w-8 shrink-0 font-mono text-xs text-gray-500">{{ query.key }}</span>
                <div class="min-w-0 flex-1" :class="{ 'opacity-55': !query.active }">
                  <p v-for="number in query.covers_items" :key="number" class="truncate text-sm" :data-testid="`checklist-query-line-${query.key}-${number}`">
                    <span class="mr-2 font-mono text-xs font-semibold text-gray-600">{{ number }}</span>{{ itemTitle(number) }}
                  </p>
                  <p v-if="query.covers_items.length === 0" class="text-sm text-gray-500">{{ t('messages.admin.report_qc.checklist.no_item_chosen') }}</p>
                  <p
                    v-for="problem in problemsFor('query', query.key)"
                    :key="problem.field + problem.code"
                    class="mt-1 text-sm text-red-600"
                    :data-testid="`checklist-query-error-${query.key}`"
                  >
                    {{ problem.message }}
                  </p>
                </div>
                <ToggleSwitch
                  v-model="query.active"
                  :aria-label="t('messages.admin.report_qc.checklist.active')"
                  :data-testid="`checklist-query-switch-${query.key}`"
                  @click.stop
                />
              </li>
            </ul>
          </template>
        </Card>

        <Card :class="{ 'border-l-4 border-amber-400': dirty && overLimit }" data-testid="checklist-save-bar">
          <template #content>
            <Fluid>
              <p v-if="dirty && preview" class="mb-1 text-sm font-medium" data-testid="checklist-impact">{{ impact }}</p>
              <p v-if="dirty && overLimit" class="mb-3 text-sm text-amber-800" data-testid="checklist-d2">{{ t('messages.admin.report_qc.checklist.d2') }}</p>
              <label for="rqc-checklist-note" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.note') }}</label>
              <Textarea id="rqc-checklist-note" v-model="form.note" rows="2" auto-resize :invalid="!!form.errors.note" data-testid="checklist-note" />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.note }" data-testid="checklist-note-error">{{ form.errors.note || '&nbsp;' }}</p>
              <p v-for="message in otherErrors" :key="message" class="text-sm text-red-600" data-testid="checklist-server-error">{{ message }}</p>
              <p v-if="dirty && problems.length" class="text-sm text-red-600" data-testid="checklist-save-blocked">
                {{ t('messages.admin.report_qc.checklist.save_blocked', { n: problems.length }) }}
              </p>
              <div class="mt-2 flex flex-wrap items-end justify-end gap-2">
                <ChecklistTrial
                  :draft="() => ({ items: draft.items, queries: draft.queries })"
                  :draft-hash="dirty ? (preview?.hash ?? null) : trial.currentHash"
                  :disabled="form.processing || (dirty && (previewLoading || problems.length > 0))"
                  :trial="trial"
                />
                <Button
                  :label="t('messages.admin.report_qc.settings.save')"
                  icon="pi pi-check"
                  class="w-auto!"
                  :loading="form.processing"
                  :disabled="!canSave"
                  data-testid="checklist-save"
                  @click="save"
                />
              </div>
            </Fluid>
          </template>
        </Card>
      </div>

      <aside v-if="showPreview" class="w-full xl:sticky xl:top-4 xl:w-[28rem] xl:shrink-0">
        <ChecklistPreviewPanel :preview="preview" :loading="previewLoading" :failed="previewFailed" />
      </aside>
    </div>

    <ChecklistItemDrawer
      v-model:visible="itemDrawer.visible"
      :number="itemDrawer.number"
      :is-new="itemDrawer.isNew"
      :focus-key="itemDrawer.focusKey"
      :feedback-version="itemDrawer.feedbackVersion"
      :current-version="checklist.versionNumber"
      @apply="applyDrawer(itemDrawer)"
      @cancel="cancelDrawer(itemDrawer)"
    />
    <ChecklistHistoryDrawer v-model:visible="historyOpen" :versions="versions" :transfer="checklist.transfer" @import="importFile" />

    <Dialog
      v-model:visible="importOpen"
      modal
      :closable="false"
      :header="imported ? t('messages.admin.report_qc.transfer.dialog_title', { host: imported.host, n: imported.number }) : ''"
      class="mx-4 w-full max-w-2xl"
      data-testid="checklist-import-dialog"
    >
      <div v-if="imported" class="flex flex-col gap-4">
        <p class="text-sm text-gray-700" data-testid="checklist-import-source">
          {{ t('messages.admin.report_qc.transfer.dialog_source', { host: imported.host, n: imported.number, date: imported.date }) }}
          <template v-if="imported.settingsVersion">{{ t('messages.admin.report_qc.transfer.dialog_settings', { n: imported.settingsVersion }) }}</template>
        </p>

        <p v-if="previewLoading || !preview" class="text-sm text-gray-500" data-testid="checklist-import-checking">{{ t('messages.admin.report_qc.transfer.checking') }}</p>
        <template v-else>
          <Message v-if="importHashMatches" severity="success" :closable="false" data-testid="checklist-import-hash-ok">
            {{ t('messages.admin.report_qc.transfer.hash_ok') }}
          </Message>
          <Message v-else severity="error" :closable="false" data-testid="checklist-import-hash-mismatch">
            {{ t('messages.admin.report_qc.transfer.hash_mismatch', { file: imported.hash, here: preview.hash }) }}
          </Message>

          <div v-if="problems.length" class="text-sm text-red-600" data-testid="checklist-import-problems">
            <p class="font-medium">{{ t('messages.admin.report_qc.transfer.problems') }}</p>
            <ul class="list-disc pl-5">
              <li v-for="problem in problems" :key="problem.field + problem.code">{{ problem.message }}</li>
            </ul>
          </div>

          <div data-testid="checklist-import-changes">
            <p class="mb-1 text-sm font-medium">{{ t('messages.admin.report_qc.transfer.changes', { n: preview.changes.length }) }}</p>
            <ChecklistChangeList v-if="preview.changes.length" :changes="preview.changes" class="max-h-80 overflow-y-auto" />
            <p v-else class="text-sm text-gray-600" data-testid="checklist-import-no-changes">{{ t('messages.admin.report_qc.transfer.no_changes') }}</p>
          </div>
        </template>

        <Fluid>
          <label for="rqc-import-note" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.note') }}</label>
          <Textarea id="rqc-import-note" v-model="form.note" rows="2" auto-resize :invalid="!!form.errors.note" data-testid="checklist-import-note" />
          <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.note }">{{ form.errors.note || '&nbsp;' }}</p>
          <p v-for="message in otherErrors" :key="message" class="text-sm text-red-600" data-testid="checklist-import-server-error">{{ message }}</p>
        </Fluid>
      </div>
      <template #footer>
        <Button :label="t('messages.admin.report_qc.transfer.cancel')" severity="secondary" outlined class="w-auto!" data-testid="checklist-import-cancel" @click="resetDraft" />
        <Button
          :label="t('messages.admin.report_qc.settings.save')"
          icon="pi pi-check"
          class="w-auto!"
          :loading="form.processing"
          :disabled="!canSave"
          data-testid="checklist-import-save"
          @click="save"
        />
      </template>
    </Dialog>
  </div>
</template>
